import {React, observer, useEffect, stringify, Fragment, Popover2, useAsObservableSource, Tooltip2} from '@api'
import {Button, Menu, MenuDivider, MenuItem, Popover, Spinner} from '@blueprintjs/core'
import {action, autorun, computed, observable, toJS} from 'mobx'
import {bpMenu as bpMenuStyle} from './SiteHeader.css'
import * as css from './SeasonSelectorFilterDropdownModal.css'
import {SeasonSelectorFieldConfig, StoresInstance} from '@stores'
import {C8HierarchyStore, CatalogItemSidebarDetailsStore, FilterGroupState, ICatalogHierarchySelectionStateGroups, useVipLocalStore, VipLocalStore} from '@modules'
import {useCatalogItemSidebarDetailsStore} from '@modules'
import classNames from 'classnames'
import { Tooltip } from '@material-ui/core'

export class SeasonSelectorFilterDropdownModalStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: { detailsStore: CatalogItemSidebarDetailsStore, fieldConfig: SeasonSelectorFieldConfig }) {
    super(stores)

    stores.catalog.seasonSelectorsModal[this.field.field] = this
    this.c8hierarchy = new C8HierarchyStore(stores.catalog, props.detailsStore)
  }

  @observable.ref c8hierarchy: C8HierarchyStore

  get field() {return this.props.fieldConfig}
  get data() { return this.details.data}
  get details() {return this.props.detailsStore}

  registerEffects = () => {
    const toDispose = [
    ]

    return () => toDispose.forEach(d => {
      d()
      this.c8hierarchy?.dispose()
    })
  }

  @computed get isDisabled() {
    const {c8hierarchy: {isFieldDisabledOnceInC8}, details: {isOffline}, state, state: {selected}, field: {field}} = this
    return _.isEmpty(state) ? true : isFieldDisabledOnceInC8(field) && !isOffline && selected?.size != 0
  }

  @computed get invalidState() {
    const {field: {field}, details: {invalidFields}} = this
    return invalidFields[field]
  }

  @computed get isValid() {
    return this.invalidState == null
  }

  /*

   */
  @action onSelection = (field, id, name) => {
    const {dependants, hierarchySelectionStore: {toggleDropDownFilter, resetSelection}} = this

    if (dependants && dependants.length) {
      resetSelection(dependants)
    }

    toggleDropDownFilter(field, id, {id, name, exId: id})
  }

  @computed get dependantIds() {
      const {dependentFieldSelection, isDisabled} = this

      if (!isDisabled && dependentFieldSelection) {
        const {selected, groupedByValue} = dependentFieldSelection
        return Array.from(selected.values()).map(v => v?.id).flat() as string[]
      }
      return []
  }

  get groups() : ICatalogHierarchySelectionStateGroups {
    const {c8hierarchy: {catalogHierarchySelectionStore}} = this
    return catalogHierarchySelectionStore?.dropDownSelectionState?.groups ?? {}
  }

  get dependants() {
    return this.field?.dependants
  }

  @computed get state() : FilterGroupState {
    return this.groups[this.field.field] ?? {} as FilterGroupState
  }

  @computed get dependentFieldSelection() : FilterGroupState {
    return this.groups[this.field.depends] ?? {} as FilterGroupState
  }

  @action clearFilter = () => {
    this.state.selected.clear()
  }

  get chooseMsg() {
    const {field} = this,
      label = field.label ?? field.field

    return `Choose a ${_.startCase(label)}...`
  }

  @computed get currentName() {
    return _.get(this.data, `${this.field.field}.name`)
  }

  @computed get buttonText() {
    const {state: {selected}, field, visible, chooseMsg, currentName} = this,
      label = field.label ?? field.field

    //console.log("selected::", selected);

    return currentName ?? (
      visible?.length == 0
           ? `No ${label}`
           : selected && selected.size > 0 ? _.truncate(Array.from(selected.values()).filter(_.identity).map(({name}) => name).join(', '), {length: 18})
                               : chooseMsg)
  }

  tooltipFor = (data) => {
    const {c8hierarchy: {pathToHierarchyItem}} = this
    return _.map(_.map(data).filter(_.identity), (d: any) => ({...d, tooltip: pathToHierarchyItem(d.collectionId || d.id).map(i => i.name).join(' → ')}))
  }

  @computed get isC8DataLoaded() {
    const {c8hierarchy: {isDataLoadedForField}} = this,
        {field: {field}} = this
    return isDataLoadedForField(field)
  }

  @computed get visible() {
    const {isC8DataLoaded, c8hierarchy: {hierarchies, getMerchSeasonsForCollectionId}} = this,
      {field: {field}, tooltipFor} = this

    return isC8DataLoaded ?
           (
             _.size(Array.from(this.dependantIds)) ? tooltipFor(getMerchSeasonsForCollectionId(field, this.dependantIds)) : tooltipFor(_.uniqBy(hierarchies[field], 'name'))
           ) : []
  }

  @computed get hierarchySelectionStore() { return this.c8hierarchy.catalogHierarchySelectionStore}

  @computed get invalidStateTooltip() {
    const {catalog: {labelForField}, props: {fieldConfig}, dependentFieldSelection, dependants, field, invalidState} = this

    // invalidState && console.log({dependentFieldSelection, dependants, field, invalidState})

    // return 'invalidStateTooltip'

    return `No values in C8 for ${labelForField(fieldConfig)}`
  }

  @computed get dataSelected() {
    return [...this.state.selected?.keys?.() ?? []].join(', ')
  }
}

export const SeasonSelectorFilterDropdownModal = observer<{ field?: SeasonSelectorFieldConfig }>(
  function SeasonSelectorFilterDropdownModal({field: fieldConfig}) {
    const detailsStore = useCatalogItemSidebarDetailsStore(),
      {invalidFields, isOffline} = detailsStore,
      obsProps = useAsObservableSource({detailsStore, fieldConfig}),
      store = useVipLocalStore(
        (ws) => new SeasonSelectorFilterDropdownModalStore(ws, obsProps)),
      {
        field: {field}, chooseMsg, dependentFieldSelection, visible, buttonText, state: {selected}, clearFilter, onSelection,
        c8hierarchy: {isDataLoadedForField}, registerEffects, isDisabled, state, isValid, dataSelected
      } = store

    useEffect(() => registerEffects(), [])

    if (_.isEmpty(state)) return null

    // console.log(selected, visible)
    return <div className={css.root}
                data-visible={isDataLoadedForField(field)}
                data-type='SeasonSelectorDropdown'
                data-field={field}
                data-dependent-fields={dependentFieldSelection && JSON.stringify(dependentFieldSelection)}
                data-selected={dataSelected}>
      <Popover2
        position='bottom-right'
        interactionKind='hover'
        minimal
        className={css.popover}
        disabled={(isDisabled || visible?.length === 0)}
        content={
          <Menu className={classNames(bpMenuStyle, css.modalMenu)}>
            {visible?.map(({id, name}) =>
              <MenuItem
                key={id}
                text={name} labelElement={``}
                icon={selected?.has(name) ? 'tick' : 'blank'}
                onClick={() => onSelection(field, id, name)}/>
            )}
            {visible?.length && selected.size ? <Fragment>
              <MenuDivider/>
              <MenuItem icon='blank' text='Clear' onClick={clearFilter}/>
            </Fragment> : null}
          </Menu>
        }>
        <Button
          disabled={isDisabled}
          data-valid={isValid}
          minimal
          className={css.button}
          text={!isDataLoadedForField(field) ? <Spinner size={14}/> : <span className={css.trim}>{_.isEmpty(buttonText) ? chooseMsg : buttonText}</span>}
          rightIcon='caret-down'/>
      </Popover2>
    </div>
  }
)



// WEBPACK FOOTER //
// ./src/modules/siteHeader/components/SeasonSelectorFilterDropdownModal.tsx